import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { fetchFastApi } from "../../api/fastapi";
import { consumePostLoginRedirect } from "../../lib/authRedirect";
import { LoadingSpinner } from "../../components/ui/loadingSpinner/loadingSpinner";
import { useAuth } from "../../contexts/authContext.tsx";
import { useCheckTokenAndFetchUser } from "../../hooks/useCheckTokenAndFetchUser.ts";

export const SSOCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { setAuth } = useAuth();
  const { checkAndRefreshToken, fetchUser } = useCheckTokenAndFetchUser();
  const codeExchangeStartedRef = useRef(false);

  useEffect(() => {
    // The WorkOS authorization code is single-use; guard so the exchange runs
    // exactly once per page load even if effect dependencies change identity.
    if (codeExchangeStartedRef.current) {
      return;
    }
    codeExchangeStartedRef.current = true;

    const handleCallback = async () => {
      try {
        // Extract parameters from URL
        const code = searchParams.get("code");
        const error = searchParams.get("error");
        const errorDescription = searchParams.get("error_description");
        const errorUri = searchParams.get("error_uri");

        // Handle error case
        if (error) {
          console.error("SSO error:", { error, errorDescription, errorUri });
          setError(
            errorDescription ||
              error ||
              "An error occurred during SSO authentication",
          );
          setLoading(false);
          return;
        }

        // Handle missing code
        if (!code) {
          setError("Missing authorization code from SSO provider");
          setLoading(false);
          return;
        }

        // Send code to backend
        const response = await fetchFastApi({
          method: "GET",
          path: `/auth/sso/callback?code=${encodeURIComponent(code)}`,
        });

        // todo remove log
        console.log("Received callback response");
        console.log(response);

        // Handle backend errors
        if (response.error || response.errorDescription) {
          setError(
            response.errorDescription ||
              response.error ||
              "Authentication failed",
          );
          setLoading(false);
          return;
        }

        // Handle successful authentication
        if (response.accessToken && response.refreshToken && response.idToken) {
          // todo should store in localStorage or cookies?
          // Store tokens in localStorage
          localStorage.setItem("access_token", response.accessToken);
          localStorage.setItem("refresh_token", response.refreshToken);
          localStorage.setItem("id_token", response.idToken);

          console.log("Refreshing access token");
          const accessToken = await checkAndRefreshToken();
          console.log("Refreshed access token.");
          console.log(accessToken);
          if (accessToken) {
            const {
              parsedUser,
              parsedWorkspace,
              parsedWorkspaceMember,
              parsedWorkspaces,
              parsedAnalyticsDestination,
              parsedAnalyticsIdentity,
            } = await fetchUser();
            console.log("Fetched user");
            if (parsedUser && parsedWorkspace && parsedWorkspaceMember) {
              console.log(
                "Setting auth to user and workspace",
                parsedUser,
                parsedWorkspace,
                parsedWorkspaceMember,
              );
              setAuth({
                user: parsedUser,
                loading: false,
                accessToken,
                workspace: parsedWorkspace,
                workspaceMember: parsedWorkspaceMember,
                workspaces: parsedWorkspaces,
                analyticsDestination: parsedAnalyticsDestination,
                analyticsIdentity: parsedAnalyticsIdentity,
              });
            } else if (parsedUser) {
              console.log("Setting auth to user", parsedUser);
              setAuth({
                user: parsedUser,
                loading: false,
                accessToken,
                workspace: null,
                workspaceMember: null,
                workspaces: parsedWorkspaces,
                analyticsDestination: parsedAnalyticsDestination,
                analyticsIdentity: parsedAnalyticsIdentity,
              });
            } else {
              console.log("Setting auth to null", parsedUser);
              setAuth({
                user: null,
                loading: false,
                accessToken: null,
                workspace: null,
                workspaceMember: null,
                workspaces: [],
                analyticsDestination: null,
                analyticsIdentity: null,
              });
            }

            // Redirect to the originally requested URL, else the default app.
            navigate(consumePostLoginRedirect("/audit"), { replace: true });
          } else {
            setError("Something went wrong in handling callback");
          }
        } else {
          setError("Invalid response from authentication server");
          setLoading(false);
        }
      } catch (err) {
        console.error("SSO callback error:", err);
        setError("An unexpected error occurred during authentication");
        setLoading(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate, checkAndRefreshToken, fetchUser, setAuth]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <LoadingSpinner />
          </div>
          <h2 className="mt-4 text-lg font-semibold text-gray-900">
            Completing sign in...
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Please wait while we finish authenticating you.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              Authentication Failed
            </h3>
            <p className="mt-2 text-sm text-gray-600">{error}</p>
            <div className="mt-6">
              <button
                onClick={() => navigate("/signin", { replace: true })}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Return to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
